import { TAG_EXPANSIONS, THEME_MAPPINGS } from "../constants/categories";
import { FEATURED_PODCASTS } from "../constants/featuredPodcasts";
import { getStandardPodcastCategories } from "../constants/standardPodcasts";
import {
  Podcast,
  Episode,
  SeedPod,
  Station,
  StationSource,
  HomeRow,
  PodcastIndexFeed,
  Rss2JsonData,
  Rss2JsonItem,
  SupabasePodcastRow,
} from "../types";

import { SEED_PODS } from "./data/seedPods";
import {
  fetchGlobalCarouselsForCountry,
  carouselToHomeRow,
} from "./globalCarouselService";
import { supabase } from "./supabase";
import { getUserHomePreferences } from "./userLibraryService";

// --- CACHE CONFIGURATION ---
const CACHE_PREFIX = "se_pod_cache_v3_"; // Bumped version to invalidate old cache

/**
 * Fetches XML content via our server-side API route.
 * This bypasses CORS restrictions entirely.
 */
export const fetchXmlViaServer = async (targetUrl: string): Promise<string> => {
  const apiUrl = `/api/rss-proxy?url=${encodeURIComponent(targetUrl)}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

  try {
    const res = await fetch(apiUrl, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!res.ok) {
      throw new Error(`Server fetch failed: ${res.status} `);
    }

    const text = await res.text();

    // Basic validation
    if (
      text.includes("<rss") ||
      text.includes("<channel") ||
      text.includes("<?xml") ||
      text.includes("<feed")
    ) {
      return text;
    } else {
      throw new Error("Invalid XML response");
    }
  } catch (e: unknown) {
    clearTimeout(timeoutId);
    if (e instanceof Error) {
      throw new Error(`Feed fetch failed: ${e.message} `);
    }
    throw new Error(`Feed fetch failed: ${String(e)} `);
  }
};

/**
 * Fallback: Fetches via RSS2JSON API.
 */
const fetchJsonViaRss2Json = async (
  targetUrl: string,
): Promise<Rss2JsonData> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const apiUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(targetUrl)}`;
    const res = await fetch(apiUrl, { signal: controller.signal });
    clearTimeout(timeoutId);

    if (!res.ok) throw new Error(`RSS2JSON status: ${res.status}`);
    const data = await res.json();
    if (data.status !== "ok")
      throw new Error("RSS2JSON returned status 'error'");
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * In-flight request cache to prevent duplicate API calls.
 * Maps request key to pending Promise.
 */
const inFlightRequests = new Map<string, Promise<Podcast[]>>();

/**
 * Search PodcastIndex via our server-side proxy.
 * Deduplicates concurrent requests for the same query+lang combination.
 */
export const searchPodcastIndex = async (
  query: string,
  lang?: string,
): Promise<Podcast[]> => {
  // Create a unique cache key for this request
  const cacheKey = `${query}::${lang || ""}`;

  // If there's already an in-flight request for this exact query, return it
  const existingRequest = inFlightRequests.get(cacheKey);
  if (existingRequest) {
    console.log(`[searchPodcastIndex] Deduplicating request for: ${cacheKey}`);
    return existingRequest;
  }

  // Create the actual request promise
  const requestPromise = (async (): Promise<Podcast[]> => {
    try {
      let url = `/api/podcast-index?route=search/byterm&q=${encodeURIComponent(query)}`;
      if (lang) {
        url += `&lang=${lang}`;
      }
      const res = await fetch(url);
      if (!res.ok) {
        const errText = await res.text();
        console.error("PodcastIndex Search Error Body:", errText);
        throw new Error(`API Error: ${res.status} - ${errText}`);
      }

      const data = await res.json();
      if (!data.feeds) return [];

      return data.feeds.map((feed: PodcastIndexFeed) => ({
        id: `pod-${feed.id}`,
        title: feed.title,
        description: feed.description,
        imageUrl: feed.image,
        rssUrl: feed.url,
        country: feed.country || "International",
        language: feed.language,
        status: "active",
        episodes: [],
        qualityScore: 100,
        source: "auto",
        internalCategory: "other",
        categories: Object.values(feed.categories || {}) as string[],
        totalEpisodes: feed.episodeCount,
      }));
    } catch (e) {
      console.error("PodcastIndex search failed:", e);
      return [];
    } finally {
      // Clean up the in-flight cache after request completes
      inFlightRequests.delete(cacheKey);
    }
  })();

  // Store the promise in the cache
  inFlightRequests.set(cacheKey, requestPromise);

  return requestPromise;
};

/**
 * Search podcasts via PodcastIndex API (User Requested Function)
 * Returns a simplified object structure.
 */
export const searchPodcasts = async (
  term: string,
): Promise<
  {
    id: number;
    title: string;
    description: string;
    image: string;
    feedUrl: string;
  }[]
> => {
  try {
    const res = await fetch(
      `/api/podcast-index?route=search/byterm&q=${encodeURIComponent(term)}`,
    );
    if (!res.ok) throw new Error(`API Error: ${res.status}`);

    const data = await res.json();
    if (!data.feeds) return [];

    return data.feeds.map((feed: PodcastIndexFeed) => ({
      id: feed.id,
      title: feed.title,
      description: feed.description,
      image: feed.image,
      feedUrl: feed.url,
    }));
  } catch (e) {
    console.error("searchPodcasts failed:", e);
    return [];
  }
};

/**
 * Get trending podcasts from PodcastIndex.
 */
export const getTrendingPodcasts = async (
  limit = 20,
  lang?: string,
): Promise<Podcast[]> => {
  try {
    let url = `/api/podcast-index?route=podcasts/trending&max=${limit}`;
    if (lang) url += `&lang=${lang}`;

    const res = await fetch(url);
    if (!res.ok) {
      const errText = await res.text();
      console.error("PodcastIndex Trending Error Body:", errText);
      throw new Error(`API Error: ${res.status} - ${errText}`);
    }

    const data = await res.json();
    if (!data.feeds) return [];

    return data.feeds.map((feed: PodcastIndexFeed) => ({
      id: `pod-${feed.id}`,
      title: feed.title,
      description: feed.description,
      imageUrl: feed.image,
      rssUrl: feed.url,
      country: feed.itunesId ? "International" : "Unknown", // Trending doesn't always return country
      language: feed.language,
      status: "active",
      episodes: [],
      qualityScore: 100,
      source: "auto",
      internalCategory: "other",
      categories: Object.values(feed.categories || {}) as string[],
      totalEpisodes: feed.episodeCount, // Map episode count if available
    }));
  } catch (e) {
    console.error("PodcastIndex trending failed:", e);
    // Fallback: Search for "podcast" to show something
    console.log("Falling back to generic search...");
    return searchPodcastIndex("podcast");
  }
};

/**
 * Fetch podcast metadata (specifically RSS URL) from PodcastIndex by ID.
 * Used when we have an ID but no local seed data.
 */
export const fetchPodcastFromIndex = async (
  id: string,
): Promise<SeedPod | null> => {
  try {
    const res = await fetch(
      `/api/podcast-index?route=podcasts/byfeedid&id=${id}`,
    );
    if (!res.ok) throw new Error("API Error");
    const data = await res.json();
    const feed = data.feed;
    if (!feed) return null;

    return {
      id: feed.id.toString(),
      title: feed.title,
      rssUrl: feed.url,
      imageUrl: feed.image,
      country: feed.country || "International",
      language: feed.language,
      categories: Object.values(feed.categories || {}) as string[],
      internalCategory: "other",
      source: "auto",
      description: feed.description, // Include description from API
    };
  } catch (e) {
    console.error("Failed to fetch from index:", e);
    return null;
  }
};

interface PodcastIndexEpisodeRaw {
  id: number;
  title: string;
  link: string;
  description: string;
  guid: string;
  datePublished: number;
  datePublishedPretty: string;
  dateCrawled: number;
  enclosureUrl: string;
  enclosureType: string;
  enclosureLength: number;
  duration: number;
  explicit: number;
  episode: number | null;
  episodeType: string;
  season: number | null;
  image: string;
  feedImage: string;
  feedId: number;
  feedLanguage: string;
  chaptersUrl: string | null;
  transcriptUrl: string | null;
}

interface PodcastIndexEpisodesResponse {
  status: string;
  items: PodcastIndexEpisodeRaw[];
  count: number;
  query: string;
  description: string;
}

/**
 * Normalizes a raw PodcastIndex episode into our internal Episode format.
 */
const normalizePodcastIndexEpisode = (raw: PodcastIndexEpisodeRaw): Episode => {
  return {
    title: raw.title || "Untitled Episode",
    description: (raw.description || "").replace(/<[^>]*>/g, "").slice(0, 5000),
    pubDate: raw.datePublished
      ? new Date(raw.datePublished * 1000).toISOString()
      : new Date().toISOString(),
    audioUrl: raw.enclosureUrl
      ? raw.enclosureUrl.replace("http://", "https://")
      : "",
    duration: raw.duration?.toString() || "",
    guid: raw.guid,
    imageUrl: raw.image || raw.feedImage || undefined,
  };
};

/**
 * Fetch episodes for a podcast from PodcastIndex API.
 */
export const fetchEpisodesFromIndex = async (
  feedId: string,
  limit = 20,
): Promise<Episode[]> => {
  try {
    const res = await fetch(
      `/api/podcast-index?route=episodes/byfeedid&id=${feedId}&max=${limit}`,
    );
    if (!res.ok) throw new Error(`API Error: ${res.status}`);

    const data: PodcastIndexEpisodesResponse = await res.json();

    // Robust check for items array
    const items = Array.isArray(data.items) ? data.items : [];

    console.log(
      `[fetchEpisodesFromIndex] Fetched ${items.length} episodes for feed ${feedId}`,
    );

    return items
      .map(normalizePodcastIndexEpisode)
      .filter((ep) => !!ep.audioUrl); // Ensure we only return episodes with audio
  } catch (e) {
    console.error("Failed to fetch episodes from index:", e);
    return [];
  }
};

const isPodcastActive = (pod: Podcast): boolean => {
  if (!pod.episodes || pod.episodes.length === 0) return false;

  const lastEp = pod.episodes[0];
  if (!lastEp.pubDate) return false;

  const lastDate = new Date(lastEp.pubDate);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - lastDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Consider active if updated within last 2 years (approx 730 days)
  return diffDays < 730;
};

/**
 * Normalizes parsed XML data into our Podcast format.
 */
export const normalizeXml = (pod: SeedPod, xmlText: string): Podcast => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "text/xml");

  const channel =
    xmlDoc.querySelector("channel") || xmlDoc.querySelector("feed");
  if (!channel) throw new Error("Invalid RSS XML structure");

  const title = channel.querySelector("title")?.textContent || pod.title;

  let description =
    channel.querySelector("description")?.textContent ||
    channel.querySelector("subtitle")?.textContent ||
    "";
  const docDiv = document.createElement("div");
  docDiv.innerHTML = description;
  description = docDiv.textContent || description || "";

  // --- HIGH RES IMAGE EXTRACTION ---
  let imageUrl = "";

  // 1. Try itunes:image (Always highest resolution)
  const itunesImg = Array.from(channel.getElementsByTagName("itunes:image"))[0];
  if (itunesImg) {
    const href = itunesImg.getAttribute("href");
    if (href) imageUrl = href;
  }

  // 2. Try googleplay:image
  if (!imageUrl) {
    const gpImg = Array.from(
      channel.getElementsByTagName("googleplay:image"),
    )[0];
    if (gpImg) {
      const href = gpImg.getAttribute("href");
      if (href) imageUrl = href;
    }
  }

  // 3. Try standard image > url
  if (!imageUrl) {
    const stdImg = channel.querySelector("image > url");
    if (stdImg && stdImg.textContent) {
      imageUrl = stdImg.textContent;
    }
  }

  // 4. Try media:thumbnail
  if (!imageUrl) {
    const mediaThumbnail = channel.getElementsByTagName("media:thumbnail")[0];
    if (mediaThumbnail) {
      const url = mediaThumbnail.getAttribute("url");
      if (url) imageUrl = url;
    }
  }

  // Force HTTPS
  if (imageUrl && imageUrl.startsWith("http://")) {
    imageUrl = imageUrl.replace("http://", "https://");
  }

  // --- CURATION OVERRIDE ---
  if (pod.source === "manual" && pod.imageUrl) {
    imageUrl = pod.imageUrl;
  } else if (!imageUrl || imageUrl.length < 5) {
    // Smart Fallback Logic
    if (pod.country === "Sweden" || pod.language === "sv") {
      imageUrl = "/images/fallback/stockholm.png";
    } else if (
      pod.country === "United Kingdom" ||
      pod.country === "Great Britain" ||
      pod.language === "en-gb"
    ) {
      imageUrl = "/images/fallback/london.png";
    } else {
      // Default fallback (can be expanded later)
      imageUrl = "/images/fallback/stockholm.png"; // Temporary default until we have more
    }
  }

  // --- EPISODE EXTRACTION ---
  const items = Array.from(channel.querySelectorAll("item")).slice(0, 25);

  const episodes: Episode[] = items
    .map((item) => {
      let audioUrl = "";
      const enclosure = item.querySelector("enclosure");
      if (enclosure) {
        audioUrl = enclosure.getAttribute("url") || "";
      } else {
        const media = item.getElementsByTagName("media:content")[0];
        if (media && media.getAttribute("medium") === "audio") {
          audioUrl = media.getAttribute("url") || "";
        }
      }

      if (audioUrl && audioUrl.startsWith("http://")) {
        audioUrl = audioUrl.replace("http://", "https://");
      }

      const pubDate = item.querySelector("pubDate")?.textContent || "";
      const itemTitle =
        item.querySelector("title")?.textContent || "Unknown Episode";
      const guid = item.querySelector("guid")?.textContent || audioUrl;

      let itemDesc = item.querySelector("description")?.textContent || "";
      docDiv.innerHTML = itemDesc;
      itemDesc = docDiv.textContent || "";

      const duration =
        item.getElementsByTagName("itunes:duration")[0]?.textContent || "";

      // --- PER-EPISODE IMAGE EXTRACTION ---
      let episodeImage = "";
      const epItunesImg = item.getElementsByTagName("itunes:image")[0];
      if (epItunesImg) episodeImage = epItunesImg.getAttribute("href") || "";

      if (!episodeImage) {
        const epMediaThumb = item.getElementsByTagName("media:thumbnail")[0];
        if (epMediaThumb) episodeImage = epMediaThumb.getAttribute("url") || "";
      }

      if (episodeImage && episodeImage.startsWith("http://")) {
        episodeImage = episodeImage.replace("http://", "https://");
      }

      return {
        title: itemTitle,
        description: itemDesc.slice(0, 5000),
        pubDate,
        audioUrl,
        duration: duration,
        guid,
        imageUrl: episodeImage || undefined,
      };
    })
    .filter((ep) => ep.audioUrl.length > 0);

  // --- PODCAST LINK EXTRACTION (for website) ---
  // Get the <link> tag which contains the actual podcast website (not the RSS feed)
  let podcastLink = "";
  const linkElement = channel.querySelector("link");
  if (linkElement && linkElement.textContent) {
    podcastLink = linkElement.textContent.trim();
    // Validate it's not an RSS/feed URL
    if (
      podcastLink.includes("/feed") ||
      podcastLink.includes(".xml") ||
      podcastLink.includes("rss")
    ) {
      podcastLink = ""; // Reject feed URLs
    }
  }

  return {
    ...pod,
    title: title.trim(),
    description: description.slice(0, 5000),
    imageUrl: imageUrl,
    link: podcastLink || undefined, // NEW: Actual podcast website
    status: "active",
    episodes,
    qualityScore: 100,
  };
};

const normalizeJson = (pod: SeedPod, data: Rss2JsonData): Podcast => {
  const feed = data.feed;
  const items = data.items || [];

  const episodes: Episode[] = items
    .map((item: Rss2JsonItem) => {
      let audioUrl = item.enclosure?.link || "";
      if (audioUrl && audioUrl.startsWith("http://")) {
        audioUrl = audioUrl.replace("http://", "https://");
      }

      let episodeImage = item.thumbnail || item.enclosure?.thumbnail || "";
      if (episodeImage && episodeImage.startsWith("http://")) {
        episodeImage = episodeImage.replace("http://", "https://");
      }

      return {
        title: item.title,
        description: (item.description || "")
          .replace(/<[^>]*>/g, "")
          .slice(0, 5000),
        pubDate: item.pubDate,
        audioUrl: audioUrl,
        duration: "",
        guid: item.guid,
        imageUrl: episodeImage || undefined,
      };
    })
    .filter((ep: Episode) => ep.audioUrl);

  let img = feed.image || "";
  if (pod.source === "manual" && pod.imageUrl) {
    img = pod.imageUrl;
  } else if (!img) {
    // Smart Fallback Logic
    if (pod.country === "Sweden" || pod.language === "sv") {
      img = "/images/fallback/stockholm.png";
    } else if (
      pod.country === "United Kingdom" ||
      pod.country === "Great Britain" ||
      pod.language === "en-gb"
    ) {
      img = "/images/fallback/london.png";
    } else {
      img = "/images/fallback/stockholm.png"; // Temporary default
    }
  }

  return {
    ...pod,
    title: feed.title || pod.title,
    description: (feed.description || "")
      .replace(/<[^>]*>/g, "")
      .slice(0, 5000),
    imageUrl: img,
    link: feed.link || undefined, // NEW: Actual podcast website from RSS2JSON
    status: "active",
    episodes,
    qualityScore: 90,
  };
};

// Internal fetcher (Private)
// Internal fetcher (Private)
const fetchAndNormalizeFeed = async (pod: SeedPod): Promise<Podcast> => {
  // 1. Try Supabase Cache first
  const cached = await getPodcastEpisodes(pod.id);
  if (cached) {
    console.log(`[fetchAndNormalizeFeed] Cache hit for ${pod.id}`);
    return cached;
  }

  console.log(
    `[fetchAndNormalizeFeed] Fetching ${pod.id} from external sources...`,
  );

  // 2. NEW: Try PodcastIndex Episodes API if ID is numeric (PodcastIndex ID)
  if (pod.id.match(/^\d+$/)) {
    try {
      const episodes = await fetchEpisodesFromIndex(pod.id);
      console.log(
        `[fetchAndNormalizeFeed] PodcastIndex returned ${episodes.length} episodes for ${pod.id}`,
      );
      if (episodes.length > 0) {
        return {
          ...pod,
          episodes,
          status: "active",
          qualityScore: 100,
          description: pod.description || "",
        };
      }
    } catch (e) {
      console.warn(
        `PodcastIndex API failed for ${pod.id}, falling back to RSS...`,
        e,
      );
    }
  }

  // 3. Fallback to Server-Side Proxy (RSS)
  try {
    console.log(
      `[fetchAndNormalizeFeed] Falling back to RSS proxy for ${pod.id}: ${pod.rssUrl}`,
    );
    const xmlData = await fetchXmlViaServer(pod.rssUrl);
    const result = normalizeXml(pod, xmlData);

    // Save to cache if active
    if (isPodcastActive(result)) {
      // --- ITUNES IMAGE ENHANCEMENT ---
      // If the image is missing, a local fallback, or looks suspicious/generic, try to upgrade it via iTunes
      const isGenericImage =
        !result.imageUrl ||
        result.imageUrl.startsWith("/images/fallback") ||
        result.imageUrl.includes("stockholm.png") ||
        result.imageUrl.includes("london.png") ||
        result.imageUrl.includes("generic");

      if (isGenericImage) {
        console.log(
          `[fetchAndNormalizeFeed] Image for ${result.title} is generic/missing. Attempting iTunes lookup...`,
        );
        // We assume 'Sveriges Radio' as a likely author for context if missing, or just search by title
        const countryCode = getCountryCode(pod.country);
        const itunesImage = await fetchPodcastImageFromITunes(
          result.title,
          countryCode,
        );

        if (itunesImage) {
          console.log(
            `[fetchAndNormalizeFeed] Upgraded image for ${result.title} -> ${itunesImage}`,
          );
          result.imageUrl = itunesImage;
        }
      }
      // --------------------------------

      const cacheKey = CACHE_PREFIX + pod.id;
      try {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            timestamp: Date.now(),
            data: result,
          }),
        );
      } catch {
        // Ignore cache write errors
      }
    }
    return result;
  } catch (xmlError: unknown) {
    console.warn(
      `XML fetch failed for ${pod.id}, trying RSS2JSON...`,
      xmlError,
    );

    // 3. Fallback to RSS2JSON
    try {
      const jsonData = await fetchJsonViaRss2Json(pod.rssUrl);
      return normalizeJson(pod, jsonData);
    } catch (_jsonError: unknown) {
      console.error(`All fetch attempts failed for ${pod.id}`);
      return {
        ...pod,
        imageUrl: pod.imageUrl || "",
        description: "Unable to fetch feed",
        status: "invalid",
        episodes: [],
        qualityScore: 0,
      };
    }
  }
};

export const getFastPodcastStations = async (
  categoryFilter?: string,
  countryFilter?: string,
): Promise<Station[]> => {
  try {
    // If a category is provided, search specifically for that theme first
    if (categoryFilter) {
      const expansion =
        TAG_EXPANSIONS[categoryFilter] || categoryFilter.replace(/_/g, " ");
      const themedResults = await searchPodcastsAsStations(
        expansion,
        countryFilter,
      );
      if (themedResults.length > 0)
        return themedResults.map((p) => ({ ...p, provider: "podcast" }));
    }

    // Fetch trending podcasts directly from PodcastIndex API
    // Map country filter to language code
    let langParam = undefined;
    if (countryFilter === "Sweden") langParam = "sv";
    if (countryFilter === "Finland") langParam = "fi";
    if (countryFilter === "United Kingdom") langParam = "en";

    const trending = await getTrendingPodcasts(20, langParam);

    // Filter out podcasts with 0 episodes if count is available
    const validTrending = trending.filter(
      (p) => p.totalEpisodes === undefined || p.totalEpisodes > 0,
    );

    return validTrending.map((p) => ({
      id: p.id,
      name: p.title,
      streamUrl: "",
      // Use the countryFilter parameter instead of API's country field
      // because PodcastIndex returns "International" even for Swedish podcasts
      country: countryFilter || p.country || "International",
      countryCode:
        countryFilter === "Sweden"
          ? "SE"
          : countryFilter === "Finland"
            ? "FI"
            : countryFilter === "United Kingdom"
              ? "GB"
              : "US",
      logoUrl: p.imageUrl,
      tags: [categoryFilter || "trending", ...p.categories],
      type: "podcast",
      votes: 0,
      codec: "mp3",
      bitrate: 128,
      provider: "podcast" as StationSource,
      clickTrend: 0,
    }));
  } catch (err) {
    console.error("Error getting podcasts:", err);
    return [];
  }
};

/**
 * Fetches episodes for a specific podcast - now skips Supabase and goes directly to RSS
 */
export const getPodcastEpisodes = async (
  podcastId: string,
): Promise<Podcast | null> => {
  // Skip Supabase entirely - we fetch from RSS feed instead
  // This function now just returns null to trigger the RSS fetch in hydrateStation
  console.log(
    `[getPodcastEpisodes] Skipping Supabase for ${podcastId}, will fetch from RSS`,
  );
  return null;
};

export const hydrateStation = async (
  stationId: string,
): Promise<Station | null> => {
  const rawId = stationId.replace("pod-", "");
  console.log(`[hydrateStation] Hydrating ${stationId} (raw: ${rawId})`);

  // 1. Try Supabase 'podcasts' table (The Aquarium) first
  // We check both the full ID and the raw ID just in case
  const { data: curated } = await supabase
    .from("podcasts")
    .select("*")
    .or(`id.eq.${stationId},id.eq.${rawId}`)
    .single();

  if (curated) {
    console.log(`[hydrateStation] Found in curated library: ${curated.title}`);
    const seed: SeedPod = {
      id: curated.id,
      title: curated.title,
      rssUrl: curated.rss_url,
      imageUrl: curated.image_url,
      country: curated.country || "International",
      language: curated.language || "en",
      categories: curated.categories || [],
      internalCategory: "other",
      source: "curator",
    };
    const podcast = await fetchAndNormalizeFeed(seed);
    return mapPodcastToStation(podcast, false);
  }

  // 2. Fallback to SEED_PODS (Legacy)
  let seed = SEED_PODS.find((p) => p.id === rawId);

  // 3. Fallback to PodcastIndex Lookup (for new/trending pods)
  if (!seed) {
    seed = await fetchPodcastFromIndex(rawId);
  }

  if (!seed) return null;

  const podcast = await fetchAndNormalizeFeed(seed);
  return mapPodcastToStation(podcast, false);
};

// Map of known broken/missing images to stable URLs (Wikimedia PNG thumbnails)
// DEPRECATED: We now fetch high-res images from iTunes/Apple Podcasts
// const IMAGE_FIX_MAP: Record<string, string> = { ... };

/**
 * Helper: Map full country name to ISO code for iTunes
 */
const getCountryCode = (country: string | undefined): string => {
  switch (country) {
    case "Sweden":
      return "SE";
    case "Finland":
      return "FI";
    case "United Kingdom":
      return "GB";
    case "Great Britain":
      return "GB"; // Common var
    case "Norway":
      return "NO";
    case "Denmark":
      return "DK";
    case "Germany":
      return "DE";
    default:
      return "US";
  }
};

/**
 * Helper: Fetch high-res artwork from iTunes Search API
 */
const fetchPodcastImageFromITunes = async (
  title: string,
  countryCode: string = "US",
  author?: string,
): Promise<string | null> => {
  try {
    const query = author ? `${title} ${author}` : title;
    // Add country parameter to ensure regional podcasts (like P3 Historia) are found
    const res = await fetch(
      `https://itunes.apple.com/search?term=${encodeURIComponent(query)}&media=podcast&entity=podcast&limit=1&country=${countryCode}`,
    );

    if (!res.ok) return null;

    const data = await res.json();
    if (data.results && data.results.length > 0) {
      // Get the largest available artwork (artworkUrl600 is usually the max standard return)
      return (
        data.results[0].artworkUrl600 || data.results[0].artworkUrl100 || null
      );
    }
    return null;
  } catch (e) {
    console.warn("iTunes image fetch failed:", e);
    return null;
  }
};

export const mapPodcastToStation = (
  pod: Podcast,
  isLoading = false,
): Station => {
  const latestEp = pod.episodes?.[0];

  // Determine the best website URL:
  // 1. Use pod.link if available (actual website from RSS <link> tag)
  // 2. Fall back to rssUrl only if no link exists (last resort)
  const websiteUrl = pod.link || undefined; // Don't use rssUrl as fallback

  // Fix Images for Swedish Radio if needed
  // CHANGED: Removed hardcoded logic. We now rely on the source (iTunes/RSS) providing the correct image.
  // If specific overrides are strictly needed in the future, add them here, but avoid "generic" replacements.
  const logoUrl = pod.imageUrl;

  // Quick cleanup of common broken URLs if any slip through
  if (
    logoUrl?.includes("static-cdn.sr.se") ||
    logoUrl?.includes("sverigesradio.se/images")
  ) {
    // These are often broken or blocked, so we might want to kill them to trigger a fallback/refresh
    // But for now, let's assume the upstream fetcher handled it.
  }

  return {
    id: `pod-${pod.id}`,
    name: pod.title,
    streamUrl: latestEp?.audioUrl || "",
    country: pod.country,
    countryCode:
      pod.country === "Sweden" ? "SE" : pod.country === "Finland" ? "FI" : "US",
    logoUrl: logoUrl,
    tags: ["podcast", ...(pod.categories || [])],

    votes: pod.qualityScore || 0,
    codec: "mp3",
    bitrate: 128,
    provider: "podcast",
    clickTrend: 0,
    lastEpisodeTitle: latestEp?.title,
    lastEpisodeDate: latestEp?.pubDate,
    description: pod.description,
    language: [pod.language],
    onlineStatus: "online",
    website: websiteUrl, // Now uses actual website, not RSS feed
    feedUrl: pod.rssUrl, // Keep RSS URL separate for podcast player
    state: pod.state,
    episodes: pod.episodes || [],
    isLoadingData: isLoading,
  };
};

// Removed unused local buildCategoriesForCountry.
// Now using ../constants/standardPodcasts

const BLACKLIST_TITLES = [
  "JOANNA YOULE's Podcast",
  "Talous & Yhteiskunta",
  "T&Y PODCAST",
  "Taloudesta ja yhteiskunnasta",
  "De Quoi On Parlait Déjà?",
  "En Blanco y Negro Podcast",
  "Posko",
  "CoolCast",
  "Riko Podcast",
];

export const searchPodcastsAsStations = async (
  query: string,
  countryFilter?: string,
): Promise<Station[]> => {
  // Extensive mapping of countries to language codes
  const COUNTRY_LANG_MAP: Record<string, string> = {
    // Nordics
    Sweden: "sv",
    Finland: "fi",
    Norway: "no",
    Denmark: "da",
    Iceland: "is",
    // Europe
    "United Kingdom": "en",
    Germany: "de",
    France: "fr",
    Spain: "es",
    Italy: "it",
    Netherlands: "nl",
    Poland: "pl",
    Portugal: "pt",
    Russia: "ru",
    Ukraine: "uk",
    Belgium: "fr",
    Austria: "de",
    Switzerland: "de",
    Ireland: "en",
    Greece: "el",
    "Czech Republic": "cs",
    Hungary: "hu",
    Romania: "ro",
    Bulgaria: "bg",
    // Americas
    "United States": "en",
    Canada: "en",
    Mexico: "es",
    Brazil: "pt",
    Argentina: "es",
    Chile: "es",
    Colombia: "es",
    Peru: "es",
    Venezuela: "es",
    // Asia / Oceania
    Australia: "en",
    "New Zealand": "en",
    Japan: "ja",
    China: "zh",
    "South Korea": "ko",
    India: "hi",
    Indonesia: "id",
    Turkey: "tr",
    Thailand: "th",
    Vietnam: "vi",
    // Middle East / Africa
    Israel: "he",
    "Saudi Arabia": "ar",
    Egypt: "ar",
    "South Africa": "en",
    Nigeria: "en",
  };

  let langParam: string | undefined = undefined;
  let finalQuery = query;

  if (countryFilter && countryFilter !== "All") {
    const mappedLang = COUNTRY_LANG_MAP[countryFilter];
    if (mappedLang) {
      langParam = mappedLang;
    } else {
      // Fallback: Append country name if no language map found
      finalQuery = `${query} ${countryFilter}`;
    }
  }

  const podcasts = await searchPodcastIndex(finalQuery, langParam);

  // Filter out podcasts with 0 episodes AND Blacklisted titles
  const validPodcasts = podcasts.filter(
    (p) =>
      (p.totalEpisodes === undefined || p.totalEpisodes > 0) &&
      !BLACKLIST_TITLES.some((badTitle) =>
        p.title.toLowerCase().includes(badTitle.toLowerCase()),
      ),
  );

  // Map podcasts to stations and override country with the filter value
  return validPodcasts.map((p) => {
    const station = mapPodcastToStation(p);

    // Override country and countryCode with filter values if provided
    if (countryFilter && countryFilter !== "All") {
      station.country = countryFilter;
      // Map country codes roughly...
      station.countryCode =
        countryFilter === "Sweden"
          ? "SE"
          : countryFilter === "Finland"
            ? "FI"
            : "US"; // Default fallback
    }

    return station;
  });
};

/**
 * Fetches curated podcasts from the new 'podcasts' table.
 */
const NON_HTTPS_DOMAINS = ["chefpablos.com", "assets.acast.com"];
const getCuratedPodcastsFromDB = async (
  country?: string,
): Promise<Station[]> => {
  // console.log('[getCuratedPodcastsFromDB] Starting with country:', country);
  if (!supabase) {
    console.warn("[getCuratedPodcastsFromDB] Supabase client not available");
    return [];
  }
  try {
    let query = supabase.from("podcasts").select("*");

    if (country && country !== "All") {
      // Handle both country names and codes
      const countryVariants = [];
      if (country === "United States" || country === "USA") {
        countryVariants.push("United States", "USA", "US", "International");
      } else {
        countryVariants.push(country);
      }
      // Use OR condition to match any variant
      query = query.or(
        countryVariants.map((c) => `country.ilike.${c}`).join(","),
      );
    }

    const { data, error } = await query;

    if (error) throw error;
    if (!data) return [];

    const stations = data.map((row: SupabasePodcastRow) => ({
      id: row.id,
      name: row.title,
      streamUrl: "", // Will be hydrated later
      country: row.country,
      countryCode: row.country === "Sweden" ? "SE" : "US",
      logoUrl: NON_HTTPS_DOMAINS.some((d) => row.image_url?.includes(d))
        ? ""
        : row.image_url,
      tags: ["podcast", ...(row.categories || [])],
      type: "podcast",
      votes: 0,
      codec: "mp3",
      bitrate: 128,
      clickTrend: 0,
      description: row.description,
      language: [row.language],
      website: row.rss_url,
      provider: "podcast" as StationSource,
    }));

    return stations;
  } catch (e) {
    console.error("[getCuratedPodcastsFromDB] Error:", e);
    return [];
  }
};

export const streamPodcastRows = async (
  country: string,
  onRowAvailable: (row: HomeRow) => void,
  userId?: string,
): Promise<void> => {
  const seenIds = new Set<string>();

  const emitRow = (
    id: string,
    title: string,
    subtitle: string,
    stations: Station[],
    type: HomeRow["type"],
  ) => {
    const unique = stations.filter((s) => !seenIds.has(s.id));
    if (unique.length > 0) {
      unique.forEach((s) => seenIds.add(s.id));
      onRowAvailable({ id, title, subtitle, stations: unique, type });
    }
  };

  // 0. Fetch Global Carousels
  let globalCarousels = await fetchGlobalCarouselsForCountry(
    country,
    "podcast",
    userId,
  );

  // Filter out the unwanted "Podcast" carousel (temporary fix for DB item)
  globalCarousels = globalCarousels.filter(
    (c) => !(c.title === "Podcast" || c.title === "Podcasts"),
  );
  const topCarousels = globalCarousels.filter((c) => c.position === "top");
  const bottomCarousels = globalCarousels.filter(
    (c) => c.position === "bottom",
  );
  const indexedCarousels = globalCarousels.filter((c) =>
    c.position.startsWith("index:"),
  );

  // Emit Top Carousels
  for (const carousel of topCarousels) {
    const row = await carouselToHomeRow(carousel);
    if (row) onRowAvailable(row);
  }

  // 1. FEATURED PODCASTS for Hero (curated list)
  const featuredHeroStations: Station[] = [];

  // Convert featured podcasts to stations - use curated images directly
  for (const fp of FEATURED_PODCASTS.slice(0, 8)) {
    // Only add if we have an image
    if (fp.imageUrl) {
      const station: Station = {
        id: `pod-${fp.id}`,
        name: fp.name,
        streamUrl: "",
        country: fp.country,
        countryCode:
          fp.country === "United States"
            ? "US"
            : fp.country === "United Kingdom"
              ? "GB"
              : "SE",
        logoUrl: fp.imageUrl, // Always use our curated image
        tags: ["podcast", "featured", fp.category.toLowerCase()],
        votes: 1000,
        codec: "mp3",
        bitrate: 128,
        provider: "podcast",
        clickTrend: 100,
        description: fp.description,
      };
      featuredHeroStations.push(station);
    }
  }

  // Emit featured hero if we have any
  if (featuredHeroStations.length > 0) {
    emitRow(
      "hero",
      "header.featured",
      "header.featured.sub",
      featuredHeroStations,
      "curated",
    );
  }

  // 2. Fetch Trending for the "Trending Now" row (separate from hero)
  const allPods = await getFastPodcastStations(undefined, country);

  if (allPods.length > 0) {
    // Filter out any that are already in hero
    const heroIds = new Set(featuredHeroStations.map((s) => s.id));
    const trendingStations = allPods.filter(
      (p) => !heroIds.has(p.id) && p.logoUrl,
    );

    if (trendingStations.length > 0) {
      emitRow(
        "pod_trending",
        "Trending Now",
        "Hot right now",
        trendingStations.slice(0, 15),
        "trending",
      );
    }
  }

  // Buffer for standard rows to allow indexed insertion
  const standardRows: HomeRow[] = [];

  // 2. Dynamic Categories - Fetch in parallel but emit in order
  // 2. Dynamic Categories - Fetch in parallel but emit in order
  const categories = getStandardPodcastCategories(country);
  console.log(
    "[podcastService] Generated categories for",
    country,
    ":",
    categories.map((c) => c.id),
  );

  // Filter by User Preferences if user is logged in
  let finalCategories = categories;
  if (userId) {
    try {
      const prefs = await getUserHomePreferences(userId);
      const hiddenIds = new Set(
        prefs.filter((p) => !p.isVisible).map((p) => p.sectionId),
      );
      finalCategories = categories.filter((c) => !hiddenIds.has(c.id));
    } catch (e) {
      console.error("[streamPodcastRows] Failed to fetch user preferences", e);
    }
  }

  const categoryPromises = finalCategories.map(
    async (cat): Promise<HomeRow | null> => {
      try {
        let allResults = await searchPodcastsAsStations(
          cat.queries[0] || "podcast",
          country,
        ); // Use first query
        console.log(
          `[podcastService] Category ${cat.id} (${cat.title}) results:`,
          allResults.length,
        );

        // FALLBACK: If not enough localized content, fetch global content (English preferred)
        if (allResults.length < 5 && country !== "All") {
          // Determine fallback language: 'en' for everyone
          // CRITICAL FIX: Use the generic English query (2nd item) if available, otherwise 1st
          const fallbackQuery =
            cat.queries[1] && cat.queries[1].length > 0
              ? cat.queries[1]
              : cat.queries[0] || "podcast";
          const globalResultsRaw = await searchPodcastIndex(
            fallbackQuery,
            "en",
          );
          const globalResults = globalResultsRaw
            .filter((p) => p.totalEpisodes === undefined || p.totalEpisodes > 0)
            .map((p) => mapPodcastToStation(p));

          // Merge: Local first, then Global (deduplicated by ID)
          const existingIds = new Set(allResults.map((s) => s.id));
          const uniqueGlobal = globalResults.filter(
            (s) => !existingIds.has(s.id),
          );
          allResults = [...allResults, ...uniqueGlobal];
        }

        if (allResults.length > 0) {
          const finalStations = allResults.slice(0, 20);
          // Return row object instead of emitting directly
          return {
            id: cat.id,
            title: cat.title,
            subtitle: cat.sub,
            stations: finalStations,
            type: cat.type || "genre",
            filterTag: cat.filterTag,
          };
        }
      } catch (e) {
        console.error(
          `[streamPodcastRows] Failed to fetch category ${cat.title}:`,
          e,
        );
      }
      return null;
    },
  );

  const categoryRows = (await Promise.all(categoryPromises)).filter(
    (r): r is HomeRow => r !== null,
  );
  console.log(
    "[podcastService] Final category rows to emit:",
    categoryRows.map((r) => r.id),
  );
  standardRows.push(...categoryRows);

  // 3. Load Custom Podcasts from Database
  // 3. Load Custom Podcasts from Database
  const customPodcasts = await getCuratedPodcastsFromDB(country);

  if (customPodcasts.length > 0) {
    // Group custom podcasts by their tags
    const tagGroups = new Map<string, Station[]>();

    customPodcasts.forEach((pod) => {
      (pod.tags || []).forEach((tag) => {
        const normalizedTag = tag.toLowerCase().trim();

        if (!tagGroups.has(normalizedTag)) {
          tagGroups.set(normalizedTag, []);
        }
        // Avoid duplicates
        if (!tagGroups.get(normalizedTag)!.some((p) => p.id === pod.id)) {
          tagGroups.get(normalizedTag)!.push(pod);
        }
      });
    });

    // Create carousel rows for each unique tag
    tagGroups.forEach((pods, tag) => {
      if (pods.length > 0) {
        const [titlePart, subtitlePart] = tag.split("::");
        const displayTitle =
          titlePart.charAt(0).toUpperCase() + titlePart.slice(1);
        const displaySubtitle =
          subtitlePart || `Curated ${displayTitle} content`;
        const mappedTheme = THEME_MAPPINGS[tag] || tag;

        standardRows.push({
          id: `pod_custom_${tag}`,
          title: displayTitle,
          subtitle: displaySubtitle,
          stations: pods,
          type: "curated",
          filterTag: mappedTheme,
        });
      }
    });
  }

  // Insert Indexed Carousels
  for (const carousel of indexedCarousels) {
    const indexStr = carousel.position.split(":")[1];
    const index = parseInt(indexStr, 10);
    if (!isNaN(index) && index >= 0 && index <= standardRows.length) {
      const row = await carouselToHomeRow(carousel);
      if (row) {
        standardRows.splice(index, 0, row);
      }
    }
  }

  // Emit all standard rows (including inserted ones)
  standardRows.forEach((row) => {
    // Use emitRow helper logic to ensure uniqueness if needed, or just emit
    // But emitRow uses seenIds. So we should use emitRow logic.
    // But emitRow takes args, not a row object.
    // Let's just call onRowAvailable directly, but we need to filter seenIds?
    // The original emitRow filtered seenIds.
    // Let's manually filter here.
    const unique = row.stations.filter((s) => !seenIds.has(s.id));
    if (unique.length > 0) {
      unique.forEach((s) => seenIds.add(s.id));
      onRowAvailable({ ...row, stations: unique });
    }
  });

  // Emit Bottom Carousels
  for (const carousel of bottomCarousels) {
    const row = await carouselToHomeRow(carousel);
    if (row) onRowAvailable(row);
  }
};
